import {
  countSystemCombinationK,
  countSystemCombinations
} from '../../services/math'

import { logger } from '../../utils'

import { BET_EVENT_TYPE, BET_TYPE } from '../../constants'
import { getBetItemsForMulti } from '../../index'

export default class BetTypesCalculator {
  /*
    Used by betslip state provider for recounting available bet types
  */
  getAvailableBetTypes (betslipState) {
    const {
      /*
      Betslip outcomes
      */
      betItems,
      /*
        Bet types inherit a general active and suspended state
      */
      active,
      settings,
      suspended
    } = betslipState

    const betItemsForMulti = getBetItemsForMulti(betItems)

    const outcomeAmount = betItemsForMulti.length || 0

    const recountedBetTypes = [
      {
        id: 'single',
        betType: BET_TYPE.SINGLE,
        active,
        suspended,
        synchronized: false,
        ...this.countMultipleBetType({ betItems })
      }
    ]

    if (outcomeAmount < 2) {
      // Available only singles
      return recountedBetTypes
    }

    // Enabling multiple bet type
    if (settings.multiple.allowed) {
      recountedBetTypes.push({
        id: 'multiple',
        betType: BET_TYPE.MULTIPLE,
        active,
        suspended,
        synchronized: false
      })
    }

    // Enabling system bet types
    if (outcomeAmount > 2 && settings.system.allowed) {
      for (
        let winCombinationSize = 2;
        winCombinationSize < outcomeAmount;
        winCombinationSize++
      ) {
        const id = `system_${winCombinationSize}_${outcomeAmount}`

        recountedBetTypes.push({
          betType: BET_TYPE.SYSTEM,
          id,
          winCombinationSize,
          outcomeAmount,
          active,
          suspended,
          synchronized: false
        })
      }
    }

    return recountedBetTypes
  }

  mergeAvailableBetTypes (newBetTypes, prevBetTypes) {
    const output = []

    for (const availableBetTypeIndex in newBetTypes) {
      const prevBetType = prevBetTypes[availableBetTypeIndex]
      const newBetType = newBetTypes[availableBetTypeIndex]

      output.push(
        prevBetType // if available bet type exist
          ? {
            ...newBetType,
            amount: prevBetType.amount // adding prev bet type amount
          }
          : newBetType
      )
    }

    return output
  }

  countSystemBetType ({ betItems, winCombinationSize, outcomeAmount }) {
    try {
      const KBetMax = countSystemCombinationK(winCombinationSize, betItems)
      const VComb = countSystemCombinations(winCombinationSize, outcomeAmount)
      const KBet = KBetMax / VComb
      const outcomes = betItems.map((bet) => ({
        requestId: bet.requestId,
        outcomeId: bet.outcomeId,
        odds: bet.odds,
        sportService: bet.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH,
        isBespokeOutcome: !!bet.isBespokeOutcome
      }))

      return {
        odds: KBet,
        allCombinationSize: VComb,
        outcomes
      }
    } catch (error) {
      logger.error('[BetCalculator]', 'Unable to count system', error)
      return null
    }
  }

  countMultipleBetType ({ betItems, settings }) {
    // Counting Bet.Multiple odds
    if (!betItems || betItems.length < 1) {
      return {}
    }

    const isMulti = betItems.length > 1

    let odds = 1

    const outcomes = []

    for (const bet of betItems) {
      outcomes.push({
        requestId: bet.requestId,
        outcomeId: bet.outcomeId,
        odds: bet.odds,
        sportService: bet.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH,
        isBespokeOutcome: !!bet.isBespokeOutcome
      })

      const outcomeOddsForBetCalculation = isMulti
        ? bet.oddsForMulti || bet.odds // oddsForMulti - for sports; odds - racing
        : bet.odds

      odds *= Number.parseFloat(outcomeOddsForBetCalculation) || 1
    }

    if (isMulti) {
      odds = roundDown2DigitsPrecision(odds)
    }

    /*
      Creating BetType.Multiple
    */
    return {
      odds,
      outcomes
    }
  }

  calculateOddsDiff (a, b) {
    return (Math.floor(a * 1e3) - Math.floor(b * 1e3)) / 1e3
  }
}

function isDef (value) {
  return value !== undefined && value !== null
}

// 1.2389 -> 1.23
function roundDown2DigitsPrecision (value) {
  return Math.floor(value * 100) / 100
}
